import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  AlertTriangle,
  Boxes,
  CheckCircle2,
  ChevronRight,
  Loader2,
  Package,
  RefreshCw,
  Save,
  Search,
  SlidersHorizontal,
  X,
} from "lucide-react";
import { toast } from "react-toastify";
import Navbar from "../../../components/Navbar";
import VariantsStockDrawer from "../../../components/Inventory/VariantsStockDrawer";
import InventoryFilterDrawer from "../../../components/Inventory/InventoryFilterDrawer";
import InventoryPagination from "../../../components/Inventory/InventoryPagination";
import { DealsRequest } from "../../../requests/vendor/DealsRequest";
import useDebouncedValue from "../../../hooks/useDebouncedValue";
import {
  normalizeSizesFromApi,
  sumSizeQuantities,
} from "../../../utils/fashionSizes";

const DEFAULT_FILTERS = {
  category: "",
  subcategory: "",
  stock: "all",
  status: "",
  variants: "all",
  offerType: "",
};

const getStockLevel = (quantity) => {
  const qty = Math.max(0, Math.floor(Number(quantity) || 0));
  if (qty <= 0) return "out";
  if (qty <= 5) return "low";
  return "in_stock";
};

const StockBadge = ({ quantity }) => {
  const level = getStockLevel(quantity);
  const styles = {
    out: "text-rose-600",
    low: "text-amber-600",
    in_stock: "text-emerald-600",
  };
  const labels = {
    out: "Out of stock",
    low: "Low stock",
    in_stock: "In stock",
  };

  return (
    <span className={`text-xs font-medium whitespace-nowrap ${styles[level]}`}>
      {labels[level]}
    </span>
  );
};

const StatusPill = ({ status }) => {
  if (!status) return <span className="text-sm text-gray-400">—</span>;
  const normalized = String(status).toLowerCase();
  const tone =
    normalized === "active" || normalized === "approved"
      ? "bg-emerald-50 text-emerald-700"
      : normalized === "pending"
        ? "bg-amber-50 text-amber-700"
        : "bg-gray-100 text-gray-600";

  return (
    <span
      className={`inline-flex px-2 py-0.5 rounded-md text-xs font-medium capitalize ${tone}`}
    >
      {status}
    </span>
  );
};

const qtyInputClass =
  "w-14 h-8 px-2 text-sm text-center tabular-nums border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C]";

const getItemQty = (item, draft) => {
  if (draft?.sizes?.length > 0) return sumSizeQuantities(draft.sizes);
  return Math.max(0, Math.floor(Number(draft?.quantity ?? item.quantity) || 0));
};

const Inventory = () => {
  const [items, setItems] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [savingSlug, setSavingSlug] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [variantsItem, setVariantsItem] = useState(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const debouncedSearch = useDebouncedValue(searchInput, 350);

  const loadInventory = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setIsLoading(true);
    else setIsRefreshing(true);

    try {
      const response = await DealsRequest.getAllDeals();
      if (!response.success) {
        toast.error(response.message || "Failed to load inventory");
        setItems([]);
        setDrafts({});
        return;
      }

      const mapped = (response.data || []).map((deal) => {
        const sizes = normalizeSizesFromApi(deal.sizes);
        const hasSizes = sizes.length > 0;
        const quantity = hasSizes
          ? sumSizeQuantities(sizes)
          : Math.max(0, Math.floor(Number(deal.quantity) || 0));

        return {
          id: deal.id,
          slug: deal.slug || deal.deal_slug,
          title: deal.title || deal.product_name || "Untitled deal",
          sku: deal.sku || "",
          image: deal.image,
          status: deal.status || "",
          product_category: deal.product_category || "",
          product_subcategory: deal.product_subcategory || "",
          offer_type: deal.offer_type || "",
          quantity,
          sizes,
        };
      });

      const nextDrafts = {};
      mapped.forEach((item) => {
        nextDrafts[item.slug] = {
          quantity: String(item.quantity),
          sizes: item.sizes.map((row) => ({
            size: row.size,
            quantity: String(row.quantity ?? "0"),
          })),
        };
      });

      setItems(mapped);
      setDrafts(nextDrafts);
    } catch (error) {
      toast.error(error?.message || "Failed to load inventory");
      setItems([]);
      setDrafts({});
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadInventory();
  }, [loadInventory]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, filters, perPage]);

  const filterOptions = useMemo(() => {
    const statuses = new Set();
    const offerTypes = new Set();

    items.forEach((item) => {
      if (item.status) statuses.add(item.status);
      if (item.offer_type) offerTypes.add(item.offer_type);
    });

    return {
      statuses: Array.from(statuses).sort(),
      offerTypes: Array.from(offerTypes).sort(),
    };
  }, [items]);

  const filteredItems = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    return items.filter((item) => {
      const draft = drafts[item.slug];
      const currentQty = getItemQty(item, draft);

      if (filters.stock !== "all" && getStockLevel(currentQty) !== filters.stock) {
        return false;
      }
      if (filters.category && item.product_category !== filters.category) {
        return false;
      }
      if (filters.subcategory && item.product_subcategory !== filters.subcategory) {
        return false;
      }
      if (filters.status && String(item.status) !== filters.status) {
        return false;
      }
      if (filters.offerType && String(item.offer_type) !== filters.offerType) {
        return false;
      }
      if (filters.variants === "with" && !(draft?.sizes?.length > 0)) {
        return false;
      }
      if (filters.variants === "without" && draft?.sizes?.length > 0) {
        return false;
      }

      if (!q) return true;
      return (
        item.title.toLowerCase().includes(q) ||
        String(item.sku || "").toLowerCase().includes(q) ||
        String(item.slug || "").toLowerCase().includes(q) ||
        String(item.product_category || "").toLowerCase().includes(q) ||
        String(item.product_subcategory || "").toLowerCase().includes(q)
      );
    });
  }, [items, drafts, debouncedSearch, filters]);

  const paginatedItems = useMemo(() => {
    const start = (page - 1) * perPage;
    return filteredItems.slice(start, start + perPage);
  }, [filteredItems, page, perPage]);

  const stats = useMemo(() => {
    let inStock = 0;
    let low = 0;
    let out = 0;
    items.forEach((item) => {
      const level = getStockLevel(getItemQty(item, drafts[item.slug]));
      if (level === "in_stock") inStock += 1;
      else if (level === "low") low += 1;
      else out += 1;
    });
    return { total: items.length, inStock, low, out };
  }, [items, drafts]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.category) count += 1;
    if (filters.subcategory) count += 1;
    if (filters.stock !== "all") count += 1;
    if (filters.status) count += 1;
    if (filters.variants !== "all") count += 1;
    if (filters.offerType) count += 1;
    return count;
  }, [filters]);

  const clearFilters = () => setFilters(DEFAULT_FILTERS);

  const removeFilter = (key) => {
    setFilters((prev) => ({
      ...prev,
      [key]: key === "stock" || key === "variants" ? "all" : "",
      ...(key === "category" ? { subcategory: "" } : {}),
    }));
  };

  const isDirty = useCallback(
    (item) => {
      const draft = drafts[item.slug];
      if (!draft) return false;

      if (item.sizes.length > 0) {
        if (draft.sizes.length !== item.sizes.length) return true;
        return draft.sizes.some((row, idx) => {
          const original = item.sizes[idx];
          return (
            row.size !== original.size ||
            String(row.quantity) !== String(original.quantity)
          );
        });
      }

      return String(draft.quantity) !== String(item.quantity);
    },
    [drafts]
  );

  const updateQuantity = (slug, value) => {
    const cleaned = value.replace(/[^\d]/g, "");
    setDrafts((prev) => ({
      ...prev,
      [slug]: {
        ...prev[slug],
        quantity: cleaned,
      },
    }));
  };

  const applyVariantSizes = (slug, sizes) => {
    const nextSizes = (sizes || []).map((row) => ({
      size: row.size,
      quantity: String(row.quantity ?? "0"),
    }));
    setDrafts((prev) => ({
      ...prev,
      [slug]: {
        ...prev[slug],
        sizes: nextSizes,
        quantity: String(sumSizeQuantities(nextSizes)),
      },
    }));
  };

  const resetItem = (item) => {
    setDrafts((prev) => ({
      ...prev,
      [item.slug]: {
        quantity: String(item.quantity),
        sizes: item.sizes.map((row) => ({
          size: row.size,
          quantity: String(row.quantity ?? "0"),
        })),
      },
    }));
  };

  const handleSave = async (item, sizesOverride = null) => {
    const draft = drafts[item.slug];
    if (!draft && !sizesOverride) return;

    const sizes = sizesOverride || draft?.sizes || [];
    const hasSizes = sizes.length > 0;
    const quantity = hasSizes
      ? sumSizeQuantities(sizes)
      : Math.max(0, Math.floor(Number(draft?.quantity) || 0));

    if (sizesOverride) {
      applyVariantSizes(item.slug, sizesOverride);
    }

    const payload = { quantity };
    if (hasSizes) {
      payload.sizes = sizes.map((row) => ({
        size: row.size,
        quantity: String(Math.max(0, Math.floor(Number(row.quantity) || 0))),
      }));
    }

    setSavingSlug(item.slug);
    try {
      const result = await DealsRequest.updateInventory(item.slug, payload);
      if (!result.success) {
        toast.error(result.message || "Failed to update stock");
        return;
      }

      toast.success("Stock updated");
      setVariantsItem(null);
      await loadInventory({ silent: true });
    } catch (error) {
      toast.error(error?.message || "Failed to update stock");
    } finally {
      setSavingSlug(null);
    }
  };

  const stockLabel = {
    in_stock: "In stock",
    low: "Low stock",
    out: "Out of stock",
  };

  const variantsLabel = {
    with: "With variants",
    without: "No variants",
  };

  return (
    <div className="min-h-screen bg-gray-50/70">
      <Navbar page="Inventory" />

      <div className="px-4 py-6 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="flex flex-col gap-4 mb-6 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 mb-2 text-[#FF6C6C]">
              <Boxes className="w-5 h-5" />
              <span className="text-xs font-semibold tracking-wide uppercase">
                Stock management
              </span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Inventory</h1>
            <p className="mt-1 text-sm text-gray-500">
              Review and update deal stock levels.
            </p>
          </div>

          <button
            type="button"
            onClick={() => loadInventory({ silent: true })}
            disabled={isRefreshing || isLoading}
            className="inline-flex items-center self-start gap-2 px-4 py-2.5 text-sm font-semibold text-[#FF6C6C] bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-60"
          >
            <RefreshCw
              className={`w-4 h-4 ${isRefreshing ? "animate-spin" : ""}`}
            />
            Refresh
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-6 lg:grid-cols-4">
          {[
            { label: "Products", value: stats.total, icon: Package },
            { label: "In stock", value: stats.inStock, icon: CheckCircle2 },
            { label: "Low stock", value: stats.low, icon: AlertTriangle },
            { label: "Out of stock", value: stats.out, icon: Boxes },
          ].map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="p-4 bg-white border border-gray-100 rounded-xl"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-medium text-gray-500">
                    {stat.label}
                  </p>
                  <p className="mt-1 text-xl font-bold text-gray-900">
                    {stat.value}
                  </p>
                </div>
                <stat.icon className="w-4 h-4 text-gray-400" />
              </div>
            </motion.div>
          ))}
        </div>

        <div className="flex flex-col gap-3 mb-3 sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2 pointer-events-none" />
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by product, SKU, category, or slug"
              className="w-full pl-10 pr-3 py-2.5 text-sm bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C]"
            />
          </div>
          <button
            type="button"
            onClick={() => setFiltersOpen(true)}
            className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl border transition-colors ${
              activeFilterCount > 0
                ? "border-[#FF6C6C] bg-[#FF6C6C]/5 text-[#FF6C6C]"
                : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
            }`}
          >
            <SlidersHorizontal className="w-4 h-4" />
            Filters
            {activeFilterCount > 0 && (
              <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-xs font-bold text-white bg-[#FF6C6C] rounded-full">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>

        {activeFilterCount > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-4">
            {filters.category && (
              <FilterChip
                label={`Category: ${filters.category}`}
                onRemove={() => removeFilter("category")}
              />
            )}
            {filters.subcategory && (
              <FilterChip
                label={`Subcategory: ${filters.subcategory}`}
                onRemove={() => removeFilter("subcategory")}
              />
            )}
            {filters.stock !== "all" && (
              <FilterChip
                label={stockLabel[filters.stock] || filters.stock}
                onRemove={() => removeFilter("stock")}
              />
            )}
            {filters.status && (
              <FilterChip
                label={`Status: ${filters.status}`}
                onRemove={() => removeFilter("status")}
              />
            )}
            {filters.variants !== "all" && (
              <FilterChip
                label={variantsLabel[filters.variants] || filters.variants}
                onRemove={() => removeFilter("variants")}
              />
            )}
            {filters.offerType && (
              <FilterChip
                label={`Offer: ${filters.offerType}`}
                onRemove={() => removeFilter("offerType")}
              />
            )}
            <button
              type="button"
              onClick={clearFilters}
              className="text-xs font-medium text-gray-500 hover:text-[#FF6C6C]"
            >
              Clear all
            </button>
          </div>
        )}

        <div className="overflow-hidden bg-white border border-gray-200 rounded-lg">
          <div className="overflow-x-auto">
            <table className="w-full text-sm table-fixed min-w-[880px]">
              <colgroup>
                <col className="w-[30%]" />
                <col className="w-[12%]" />
                <col className="w-[14%]" />
                <col className="w-[22%]" />
                <col className="w-[22%]" />
              </colgroup>
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr className="text-left text-xs font-semibold tracking-wide text-gray-500 uppercase">
                  <th className="px-4 py-3">Product</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Available</th>
                  <th className="px-4 py-3">Variants</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {isLoading ? (
                  Array.from({ length: 6 }).map((_, idx) => (
                    <tr key={idx} className="animate-pulse">
                      <td className="px-4 py-3">
                        <div className="flex gap-3 items-center">
                          <div className="w-9 h-9 bg-gray-100 rounded" />
                          <div className="space-y-1.5 flex-1">
                            <div className="w-3/4 h-3 bg-gray-100 rounded" />
                            <div className="w-1/2 h-2.5 bg-gray-50 rounded" />
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="w-16 h-5 bg-gray-100 rounded" />
                      </td>
                      <td className="px-4 py-3">
                        <div className="w-20 h-8 bg-gray-100 rounded" />
                      </td>
                      <td className="px-4 py-3">
                        <div className="w-full h-8 bg-gray-100 rounded" />
                      </td>
                      <td className="px-4 py-3">
                        <div className="ml-auto w-16 h-8 bg-gray-100 rounded" />
                      </td>
                    </tr>
                  ))
                ) : paginatedItems.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-16 text-center">
                      <Boxes className="mx-auto mb-3 w-10 h-10 text-gray-300" />
                      <p className="font-medium text-gray-700">
                        No inventory items found
                      </p>
                      <p className="mt-1 text-sm text-gray-500">
                        Try adjusting your search or filters.
                      </p>
                      {activeFilterCount > 0 && (
                        <button
                          type="button"
                          onClick={clearFilters}
                          className="mt-3 text-sm font-medium text-[#FF6C6C] hover:underline"
                        >
                          Clear filters
                        </button>
                      )}
                    </td>
                  </tr>
                ) : (
                  paginatedItems.map((item) => {
                    const draft = drafts[item.slug] || {
                      quantity: String(item.quantity),
                      sizes: [],
                    };
                    const hasSizes = draft.sizes.length > 0;
                    const displayQty = getItemQty(item, draft);
                    const dirty = isDirty(item);
                    const saving = savingSlug === item.slug;

                    return (
                      <tr
                        key={item.slug}
                        className="hover:bg-gray-50/60 transition-colors"
                      >
                        <td className="px-4 py-3 align-middle">
                          <div className="flex gap-3 items-center min-w-0">
                            <div className="flex-shrink-0 flex items-center justify-center w-9 h-9 bg-gray-50 rounded border border-gray-100 overflow-hidden">
                              {item.image ? (
                                <img
                                  src={item.image}
                                  alt=""
                                  className="object-contain object-center w-full h-full"
                                />
                              ) : (
                                <div className="flex justify-center items-center w-full h-full text-gray-300">
                                  <Package className="w-4 h-4" />
                                </div>
                              )}
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-gray-900 truncate">
                                {item.title}
                              </p>
                              <p className="mt-0.5 text-xs text-gray-400 truncate">
                                {[
                                  item.sku ? `SKU ${item.sku}` : null,
                                  item.product_category || null,
                                  item.product_subcategory || null,
                                ]
                                  .filter(Boolean)
                                  .join(" · ") || item.slug}
                              </p>
                            </div>
                          </div>
                        </td>

                        <td className="px-4 py-3 align-middle">
                          <StatusPill status={item.status} />
                        </td>

                        <td className="px-4 py-3 align-middle">
                          <div className="flex flex-col gap-1">
                            {hasSizes ? (
                              <span className="text-sm font-semibold tabular-nums text-gray-900">
                                {displayQty}
                              </span>
                            ) : (
                              <input
                                type="text"
                                inputMode="numeric"
                                value={draft.quantity}
                                onChange={(e) =>
                                  updateQuantity(item.slug, e.target.value)
                                }
                                aria-label={`Quantity for ${item.title}`}
                                className={qtyInputClass}
                              />
                            )}
                            <StockBadge quantity={displayQty} />
                          </div>
                        </td>

                        <td className="px-4 py-3 align-middle">
                          {hasSizes ? (
                            <button
                              type="button"
                              onClick={() => setVariantsItem(item)}
                              className="group inline-flex max-w-full items-center gap-2 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-left hover:border-gray-300 hover:bg-gray-50"
                            >
                              <span className="min-w-0">
                                <span className="block text-xs font-semibold text-gray-900">
                                  {draft.sizes.length}{" "}
                                  {draft.sizes.length === 1
                                    ? "variant"
                                    : "variants"}
                                </span>
                                <span className="mt-0.5 block truncate text-[11px] text-gray-400">
                                  {draft.sizes.map((s) => s.size).join(" · ")}
                                </span>
                              </span>
                              <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-300 group-hover:text-gray-500" />
                            </button>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>

                        <td className="px-4 py-3 align-middle">
                          <div className="flex justify-end items-center gap-2 min-h-[32px]">
                            {hasSizes ? (
                              <button
                                type="button"
                                onClick={() => setVariantsItem(item)}
                                className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-semibold whitespace-nowrap text-[#FF6C6C] bg-white border border-[#FF6C6C]/40 rounded-md hover:bg-[#FF6C6C]/5"
                              >
                                Update stock
                                <ChevronRight className="w-3.5 h-3.5 flex-shrink-0" />
                              </button>
                            ) : (
                              <>
                                {dirty && (
                                  <button
                                    type="button"
                                    onClick={() => resetItem(item)}
                                    disabled={saving}
                                    className="px-2.5 h-8 text-xs font-medium whitespace-nowrap text-gray-600 hover:text-gray-900 disabled:opacity-50"
                                  >
                                    Reset
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={() => handleSave(item)}
                                  disabled={!dirty || saving}
                                  className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-semibold whitespace-nowrap text-white bg-[#FF6C6C] rounded-md hover:bg-[#e85c5c] disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {saving ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  ) : (
                                    <Save className="w-3.5 h-3.5 flex-shrink-0" />
                                  )}
                                  Save
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {!isLoading && filteredItems.length > 0 && (
            <InventoryPagination
              page={page}
              perPage={perPage}
              total={filteredItems.length}
              isFetching={isRefreshing}
              onPageChange={setPage}
              onPerPageChange={(size) => {
                setPerPage(size);
                setPage(1);
              }}
            />
          )}
        </div>
      </div>

      <InventoryFilterDrawer
        isOpen={filtersOpen}
        filters={filters}
        options={filterOptions}
        onClose={() => setFiltersOpen(false)}
        onApply={(next) => setFilters(next)}
        onClear={clearFilters}
      />

      <VariantsStockDrawer
        isOpen={Boolean(variantsItem)}
        item={variantsItem}
        initialSizes={
          variantsItem
            ? drafts[variantsItem.slug]?.sizes || variantsItem.sizes || []
            : []
        }
        isSaving={Boolean(variantsItem && savingSlug === variantsItem.slug)}
        onClose={() => {
          if (savingSlug) return;
          setVariantsItem(null);
        }}
        onApply={(sizes) => {
          if (!variantsItem) return;
          applyVariantSizes(variantsItem.slug, sizes);
          setVariantsItem(null);
        }}
        onSave={(sizes) => {
          if (!variantsItem) return;
          handleSave(variantsItem, sizes);
        }}
      />
    </div>
  );
};

const FilterChip = ({ label, onRemove }) => (
  <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-700">
    {label}
    <button
      type="button"
      onClick={onRemove}
      className="rounded-full p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
      aria-label={`Remove ${label}`}
    >
      <X className="w-3 h-3" />
    </button>
  </span>
);

export default Inventory;
